import React, { useState, useEffect, useCallback } from "react";
import { Box ,Tooltip} from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import config from "../../config.js";
import UpcomingTimeIcon from "../../images/dashboard/time_icon.svg";
import UpcomingRouteIcon from "../../images/dashboard/upcomig_route.svg";
import "./Dashboard.css";
import { getIsDemo } from "../../utils/helperFunction.js";
import scheduleJson from "../../data/schedule/schedule.json";
import CustomTooltip from "../common/Tooltip.jsx";
/**
 * DashboardUpcomingCards Component
 * Displays upcoming trip cards with vehicle, time, route, and repetition information
 * Now fetches actual stop names instead of displaying stop IDs
 * @param {Object[]} schedules - Array of schedule objects containing trip details
 */
export default function DashboardUpcomingCards({ schedules = [] }) {
  const { t } = useTranslation();
  const [stopDetails, setStopDetails] = useState(new Map()); // Cache for stop details
  const [loadingStops, setLoadingStops] = useState(new Set()); // Track which stops are being loaded
  const [retryAttempts, setRetryAttempts] = useState(new Map()); // Track retry attempts

  const parseStopTimeToMinutes = (timeValue) => {
    if (!timeValue || typeof timeValue !== "string") {
      return Number.MAX_SAFE_INTEGER;
    }

    const [hours, minutes] = timeValue.split(":").map(Number);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return Number.MAX_SAFE_INTEGER;
    }

    return hours * 60 + minutes;
  };

  /**
   * Get repetition display text
   * @param {string} repetition - The repetition type (DAILY, WEEKLY, MONTHLY, etc.)
   * @returns {string} Formatted repetition text
   */
  const getRepetitionDisplay = (repetition) => {
    if (!repetition) return "";

    switch (repetition.toUpperCase()) {
      case "DAILY":
        return t("daily", "Daily");
      case "WEEKLY":
        return t("weekly", "Weekly");
      case "MONTHLY":
        return t("monthly", "Monthly");
      default:
        return t("oneTime", "One-time");
    }
  };

  /**
   * Fetches stop details by ID from the API with retry logic
   * @param {number} stopId - The stop ID to fetch
   * @param {number} retryCount - Current retry attempt
   * @returns {Promise<Object|null>} Stop details or null if failed
   */
  const fetchStopById = async (stopId, retryCount = 0) => {

    try {
      const response = await axios.get(`${config.baseURL}/stop/id`, {
        params: { id: stopId },
        withCredentials: true,
        timeout: 10000, // 10 second timeout
      });
      return response.data;
    } catch (error) {
      console.error(
        `Error fetching stop ${stopId} (attempt ${retryCount + 1}):`,
        error
      );

      // Retry up to 2 times for failed requests
      if (
        retryCount < 2 &&
        (error.code === "ECONNABORTED" || error.response?.status >= 500)
      ) {
        console.log(
          `Retrying stop ${stopId} fetch in ${(retryCount + 1) * 1000}ms...`
        );
        await new Promise((resolve) =>
          setTimeout(resolve, (retryCount + 1) * 1000)
        );
        return fetchStopById(stopId, retryCount + 1);
      }

      return null;
    }
  };

  /**
   * Fetches stop details for multiple stop IDs with improved error handling
   * @param {number[]} stopIds - Array of stop IDs to fetch
   */
  const fetchStopDetails = useCallback(
    async (stopIds) => {
      if (!stopIds.length) return;

      // Filter out IDs that are already cached or being loaded
      const idsToFetch = stopIds.filter((id) => {
        const alreadyCached = stopDetails.has(id);
        const currentlyLoading = loadingStops.has(id);
        const maxRetriesReached = (retryAttempts.get(id) || 0) >= 3;

        return !alreadyCached && !currentlyLoading && !maxRetriesReached;
      });

      if (idsToFetch.length === 0) return;

      // console.log("Fetching stop details for IDs:", idsToFetch);

      // Mark these IDs as loading
      setLoadingStops((prev) => new Set([...prev, ...idsToFetch]));

      try {
        // Fetch stop details with controlled concurrency (max 5 at a time)
        const chunks = [];
        for (let i = 0; i < idsToFetch.length; i += 5) {
          chunks.push(idsToFetch.slice(i, i + 5));
        }

        const allResults = [];
        for (const chunk of chunks) {
          const chunkPromises = chunk.map((id) =>
            fetchStopById(id).then((data) => ({ id, data }))
          );
          const chunkResults = await Promise.all(chunkPromises);
          allResults.push(...chunkResults);
        }

        // Update cache and retry counts
        setStopDetails((prev) => {
          const newMap = new Map(prev);
          allResults.forEach(({ id, data }) => {
            if (data) {
              newMap.set(id, data);
              // console.log(`Successfully cached stop ${id}:`, data);
            } else {
              console.warn(`Failed to fetch stop ${id}`);
            }
          });
          return newMap;
        });

        setRetryAttempts((prev) => {
          const newMap = new Map(prev);
          allResults.forEach(({ id, data }) => {
            if (!data) {
              newMap.set(id, (newMap.get(id) || 0) + 1);
            }
          });
          return newMap;
        });
      } catch (error) {
        console.error("Error in batch stop fetch:", error);
      } finally {
        // Remove from loading set
        setLoadingStops((prev) => {
          const newSet = new Set(prev);
          idsToFetch.forEach((id) => newSet.delete(id));
          return newSet;
        });
      }
    },
    [stopDetails, loadingStops, retryAttempts]
  );

  /**
   * Extract all unique stop IDs from schedules and fetch their details
   */
  useEffect(() => {
    const allStopIds = new Set();

    schedules.forEach((schedule, scheduleIndex) => {
      // console.log(`Processing schedule ${scheduleIndex}:`, {
      //   id: schedule.id,
      //   stopSchedules: schedule.stopSchedules,
      // });

      if (schedule.stopSchedules && Array.isArray(schedule.stopSchedules)) {
        schedule.stopSchedules.forEach((stopSchedule, stopIndex) => {
          // console.log(`  Stop ${stopIndex}:`, {
          //   stopId: stopSchedule.stopId,
          //   arrivalTime: stopSchedule.arrivalTime,
          // });
          if (stopSchedule.stopId && !stopSchedule.stop?.name) {
            allStopIds.add(stopSchedule.stopId);
          }
        });
      } else {
        console.warn(
          `Schedule ${schedule.id} has invalid stopSchedules:`,
          schedule.stopSchedules
        );
      }
    });

    // console.log("All unique stop IDs to fetch:", [...allStopIds]);

    if (allStopIds.size > 0) {
      fetchStopDetails([...allStopIds]);
    }
  }, [schedules, fetchStopDetails]);

  /**
   * Formats time to 12-hour format with am/pm
   * @param {string} dateString - ISO date string
   * @returns {string} Formatted time string
   */
  const formatTime = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Invalid Time";
      }
      return date
        .toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        })
        .toLowerCase();
    } catch (error) {
      console.error("Error formatting time:", error);
      return "Invalid Time";
    }
  };

  /**
   * Gets stop name by ID from cache, with fallback
   * @param {number} stopId - The stop ID
   * @returns {string} Stop name or fallback text
   */
  const getStopName = (stopId) => {
    if (!stopId) {
      console.log("getStopName called with invalid stopId:", stopId);
      return t("stopNotAvailable", "Stop Not Available");
    }

    const stopData = stopDetails.get(stopId);
    const isLoading = loadingStops.has(stopId);
    const retryCount = retryAttempts.get(stopId) || 0;

    // console.log(`Getting name for stop ${stopId}:`, {
    //   stopData: stopData ? "found" : "not found",
    //   isLoading,
    //   retryCount,
    // });

    if (stopData) {
      // Return the stop name, or stop code, or any available identifier
      const name =
        stopData.name ||
        stopData.stopName ||
        stopData.code ||
        stopData.stopCode ||
        `Stop ${stopId}`;
      // console.log(`Stop ${stopId} name resolved to: ${name}`);
      return name;
    }

    if (isLoading) {
      return t("loading", "Loading...");
    }

    if (retryCount >= 3) {
      return `Stop ${stopId} (failed to load)`;
    }

    return `Stop ${stopId}`;
  };

  const resolveStopName = (stopSchedule) => {
    const inlineStopName =
      stopSchedule?.stop?.name ||
      stopSchedule?.stop?.stopName ||
      stopSchedule?.stop?.code ||
      stopSchedule?.stop?.stopCode;

    if (inlineStopName) {
      return inlineStopName;
    }

    return getStopName(stopSchedule?.stopId);
  };

  /**
   * Gets origin and destination stop information with actual names
   * @param {Object[]} stopSchedules - Array of stop schedules
   * @returns {Object} Object containing origin and destination info
   */
  const getOriginDestination = (stopSchedules) => {
    // console.log("getOriginDestination called with:", stopSchedules);

    if (!stopSchedules || stopSchedules.length === 0) {
      console.log("No stop schedules available");
      return {
        origin: t("originNotAvailable", "Origin Not Available"),
        destination: t("destinationNotAvailable", "Destination Not Available"),
        originId: null,
        destinationId: null,
      };
    }

    // Sort stops by arrival time to ensure correct order
    const sortedStops = [...stopSchedules].sort((a, b) => {
      return (
        parseStopTimeToMinutes(a.arrivalTime) -
        parseStopTimeToMinutes(b.arrivalTime)
      );
    });

    // console.log(
    //   "Sorted stops:",
    //   sortedStops.map((s) => ({
    //     stopId: s.stopId,
    //     arrivalTime: s.arrivalTime,
    //   }))
    // );

    const firstStop = sortedStops[0];
    const lastStop = sortedStops[sortedStops.length - 1];

    const result = {
      origin: resolveStopName(firstStop),
      destination: resolveStopName(lastStop),
      originId: firstStop.stopId,
      destinationId: lastStop.stopId,
    };

    // console.log("Origin-destination result:", result);
    return result;
  };

  /**
   * If no schedules are available, display a message
   */
  if (schedules.length === 0) {
    return (
      <Box className="upcoming-cards no-trips">
        <p>{t("noUpcomingTrips", "No upcoming trips scheduled")}</p>
      </Box>
    );
  }
  return (
    <>
      {schedules.map((schedule, index) => {
        // Add safety check for schedule object
        if (!schedule || !schedule.id) {
          console.warn("Invalid schedule object:", schedule);
          return null;
        }

        // console.log(
        //   `\n=== Rendering schedule ${index} (ID: ${schedule.id}) ===`
        // );
        const { origin, destination, originId, destinationId } =
          getOriginDestination(schedule.stopSchedules);
        const repetitionText = getRepetitionDisplay(schedule.repitation);

        return (
          <Box key={`${schedule.id}-${index}`} className="upcoming-cards">
            <div className="top-row">
              <div
                className="vehicle-info"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <span className="vehicle-name">
                  {/* Access vehicle name from vehicleSchedule array if it exists */}
                  {(schedule.vehicleSchedule &&
                    schedule.vehicleSchedule.length > 0 &&
                    schedule.vehicleSchedule[0].vehicle?.name) ||
                    t("vehicleNotAssigned", "Vehicle Not Assigned")}
                  {repetitionText && (
                    <span
                      className="repetition-text"
                      style={{
                        fontSize: "12px",
                        color: "#666",
                      }}
                    >
                      &nbsp; {repetitionText}
                    </span>
                  )}
                </span>
              </div>
              <div className="time">
                <CustomTooltip text="Time" position="bottom">
                  <img src={UpcomingTimeIcon} alt={t("timeAlt", "Time")} />
                </CustomTooltip>
                <span>{formatTime(schedule.startTime)}</span>
              </div>
            </div>
            <div className="bottom-row">
              <CustomTooltip text="Route" position="bottom">
                <img src={UpcomingRouteIcon} alt={t("routeAlt", "Route")} />
              </CustomTooltip>
              <div className="route-info" style={{ width: "100%" }}>
                <Tooltip
                  title={
                    schedule.route?.name ||
                    t("routeNotAvailable", "Route Not Available")
                  }
                  placement="bottom"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: "white",
                        color: "black",
                        padding: "8px 12px",
                        borderRadius: "6px",
                        fontSize: "12px",
                        whiteSpace: "nowrap",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                        border: "1px solid #A5DCF3",
                        zIndex: 10000,
                        marginTop: "5px",
                      },
                    },
                  }}
                >
                  <span
                    className="location clickable-route"
                    style={{
                      display: "flex",
                      justifyContent: "space-around",
                      alignItems: "center",
                      width: "100%",
                      color: "#1F242E",
                      fontSize: "16px",
                    }}
                  >
                    <div>{origin}</div>
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 12L16 7M21 12L16 17M21 12H3"
                        stroke="#183059"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div>{destination}</div>
                  </span>
                </Tooltip>
              </div>
            </div>
          </Box>
        );
      })}
    </>
  );
}
